/**
 * =============================================================================
 * World Blueprint - Schema de la "base" de mundo del World Builder
 * =============================================================================
 *
 * Un blueprint es la grilla MACRO que Dalmiro pinta en el World Builder: cada
 * celda cubre `cellSize` x `cellSize` tiles del mundo y describe qué predomina
 * en esa zona (bioma, camino, ciudad, densidad/tipo de vegetación, entrada de
 * dungeon). El generador procedural (`shared/world/worldGenerator.ts`) consume
 * este blueprint y produce el `MapData` real con los gráficos cargados.
 *
 * Este archivo es el CONTRATO entre el editor (cliente) y el generador (shared).
 */

import { WORLD_TILES } from '../constants';

/** Biomas disponibles (superficies indexadas en assets-index.json). */
export type Biome = 'grass' | 'sand' | 'snow' | 'water';

/** Densidad de vegetación que esparce el generador en una celda. */
export type VegetationDensity = 'none' | 'low' | 'med' | 'high';

/** Valor especial de `vegetationType`: mezcla aleatoria de especies. */
export const VEGETATION_MIXED = 'mixed' as const;

/**
 * Estado de una celda macro del blueprint.
 */
export interface BlueprintCell {
  /** Bioma/superficie que predomina en la celda. */
  biome: Biome;
  /** Si true, la celda tiene camino; se auto-conecta con celdas path adyacentes. */
  path: boolean;
  /** Si true, la celda queda pelada (sin vegetación/decoración) para ciudad futura. */
  city: boolean;
  /** Cuánta vegetación esparce el generador. */
  density: VegetationDensity;
  /**
   * Especie de vegetación que predomina. `'mixed'` (default) = aleatorio entre
   * las especies disponibles; o un id de decoración concreto (ej. 'decoration5').
   */
  vegetationType?: string;
  /** Si true, marca una entrada de dungeon (el generador emite sprite + translado). */
  dungeonEntrance?: boolean;
  /** Sprite de entrada a usar (ej. 'entrance5'). Si falta, el generador elige uno. */
  dungeonEntranceSprite?: string;
  /**
   * Si true, marca un MUELLE: se pinta sobre una celda de AGUA y el generador
   * construye la pasarela en la unión con la costa de tierra más cercana +
   * emite el área `embarkable` (solo ahí se bota/desembarca el barco).
   * Ver docs/WATER_AND_BOATS.md.
   */
  dock?: boolean;
}

/**
 * Estampa de un sector custom en el mundo (ver shared/world/sectors.ts).
 * El sector se centra en el centro de la celda (cellX, cellY) y se extiende
 * `radiusTiles` (o el default del sector si falta).
 */
export interface WorldSectorPlacement {
  /** id del sector en el registro SECTORS. */
  sectorId: string;
  /** Celda donde se centra el sector. */
  cellX: number;
  cellY: number;
  /** Radio en tiles (default: el del sector). */
  radiusTiles?: number;
}

/**
 * Blueprint completo de un mundo.
 * `cells` es row-major: el índice de (x,y) es `y * cols + x`.
 */
export interface WorldBlueprint {
  version: 1;
  /** Nombre legible (se usa como base del filename al exportar). */
  name: string;
  /** Tamaño del mundo en tiles (cuadrado). Default WORLD_TILES (1225). */
  worldTiles: number;
  /** Lado de cada celda macro en tiles. Default 35. */
  cellSize: number;
  /** Columnas = worldTiles / cellSize. */
  cols: number;
  /** Filas = worldTiles / cellSize. */
  rows: number;
  /** Semilla global determinista para la generación procedural. */
  seed: number;
  /** Celdas, row-major (length = cols * rows). */
  cells: BlueprintCell[];
  /** Sectores custom estampados en el mundo (ver shared/world/sectors.ts). */
  sectors?: WorldSectorPlacement[];
}

/** Clave de localStorage del blueprint "dev" (el que se edita siempre). */
export const BLUEPRINT_STORAGE_KEY = 'aureum:worldbuilder:dev';

/** Versión actual del schema de blueprint. */
export const BLUEPRINT_VERSION = 1 as const;

/**
 * Lado por defecto de celda macro, en tiles. Impar (35) a propósito: 35×35 celdas
 * de 35 tiles → mundo 1225 con un único centro (celda 17, tile 612). NO es múltiplo
 * de 4, así que el generador deriva los índices de bloque de superficie desde el
 * origen real en tiles de cada celda (ver `worldGenerator.ts`), no de `cellSize/4`.
 */
export const DEFAULT_CELL_SIZE = 35 as const;

/** Índice row-major de una celda (x,y). */
export function cellIndex(x: number, y: number, cols: number): number {
  return y * cols + x;
}

/** Crea una celda por defecto (pasto, sin atributos). */
export function createDefaultCell(): BlueprintCell {
  return {
    biome: 'grass',
    path: false,
    city: false,
    density: 'none',
    vegetationType: VEGETATION_MIXED,
  };
}

/**
 * Crea un blueprint vacío (todo pasto) del tamaño indicado.
 */
export function createEmptyBlueprint(
  name = 'dev',
  worldTiles: number = WORLD_TILES,
  cellSize: number = DEFAULT_CELL_SIZE,
  seed = 1
): WorldBlueprint {
  const cols = Math.floor(worldTiles / cellSize);
  const rows = Math.floor(worldTiles / cellSize);
  const cells: BlueprintCell[] = new Array(cols * rows);
  for (let i = 0; i < cells.length; i++) {
    cells[i] = createDefaultCell();
  }
  return {
    version: BLUEPRINT_VERSION,
    name,
    worldTiles,
    cellSize,
    cols,
    rows,
    seed,
    cells,
  };
}

/**
 * True si el blueprint tiene algo pintado (alguna celda distinta del default o
 * algún sector). Sirve para no sobreescribir el archivo persistido con un
 * blueprint vacío (ej. al abrir el editor en un origen sin datos).
 */
export function blueprintHasContent(bp: WorldBlueprint): boolean {
  if (bp.sectors && bp.sectors.length > 0) return true;
  return bp.cells.some(
    (c) =>
      c.biome !== 'grass' ||
      c.path ||
      c.city ||
      c.dungeonEntrance === true ||
      c.dock === true ||
      (c.density !== undefined && c.density !== 'none')
  );
}

/**
 * Valida y normaliza un blueprint parseado (ej. desde localStorage o un archivo).
 *
 * IMPORTANTE: el tamaño del mundo es un CONSTANTE global (`WORLD_TILES`) y la celda
 * macro también (`DEFAULT_CELL_SIZE`); NO se respetan los `worldTiles`/`cellSize` que
 * traiga el dato guardado. Así un blueprint viejo (ej. 32×32 cacheado en localStorage)
 * se **coacciona** a la grilla actual (35×35) al cargar, en vez de revivir el tamaño
 * viejo y desincronizar el editor del juego. Las celdas que entran en la grilla nueva
 * se preservan por posición (x,y); el resto cae a default.
 */
export function normalizeBlueprint(raw: unknown): WorldBlueprint {
  const fallback = createEmptyBlueprint();
  if (typeof raw !== 'object' || raw === null) {
    return fallback;
  }
  const obj = raw as Partial<WorldBlueprint>;
  // Dimensiones FORZADAS por las constantes globales (no por el dato guardado).
  const worldTiles = WORLD_TILES;
  const cellSize = DEFAULT_CELL_SIZE;
  const cols = Math.floor(worldTiles / cellSize);
  const rows = Math.floor(worldTiles / cellSize);
  // Dimensiones del dato viejo, para remapear las celdas por (x,y) al resize.
  const oldCols =
    typeof obj.cols === 'number' && obj.cols > 0
      ? obj.cols
      : typeof obj.worldTiles === 'number' && typeof obj.cellSize === 'number' && obj.cellSize > 0
        ? Math.floor(obj.worldTiles / obj.cellSize)
        : cols;
  const oldRows = typeof obj.rows === 'number' && obj.rows > 0 ? obj.rows : oldCols;
  const srcCells = Array.isArray(obj.cells) ? obj.cells : [];
  const cells: BlueprintCell[] = new Array(cols * rows);
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      const def = createDefaultCell();
      const c =
        x < oldCols && y < oldRows
          ? (srcCells[y * oldCols + x] as Partial<BlueprintCell> | undefined)
          : undefined;
      cells[y * cols + x] = c
        ? {
            biome: c.biome ?? def.biome,
            path: c.path ?? def.path,
            city: c.city ?? def.city,
            density: c.density ?? def.density,
            vegetationType: c.vegetationType ?? def.vegetationType,
            dungeonEntrance: c.dungeonEntrance,
            dungeonEntranceSprite: c.dungeonEntranceSprite,
            dock: c.dock,
          }
        : def;
    }
  }
  // Sectores custom: preservar las estampas válidas que caen dentro de la grilla nueva.
  const srcSectors = Array.isArray(obj.sectors) ? obj.sectors : [];
  const sectors: WorldSectorPlacement[] = [];
  for (const s of srcSectors) {
    const sp = s as Partial<WorldSectorPlacement> | undefined;
    if (
      sp &&
      typeof sp.sectorId === 'string' &&
      typeof sp.cellX === 'number' &&
      typeof sp.cellY === 'number' &&
      sp.cellX < cols &&
      sp.cellY < rows
    ) {
      sectors.push({
        sectorId: sp.sectorId,
        cellX: sp.cellX,
        cellY: sp.cellY,
        radiusTiles: typeof sp.radiusTiles === 'number' ? sp.radiusTiles : undefined,
      });
    }
  }

  return {
    version: BLUEPRINT_VERSION,
    name: typeof obj.name === 'string' ? obj.name : 'dev',
    worldTiles,
    cellSize,
    cols,
    rows,
    seed: typeof obj.seed === 'number' ? obj.seed : 1,
    cells,
    sectors,
  };
}
